"use client";

import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { TierSelect } from "./tier-select";
import { ConvictionSlider } from "./conviction-slider";
import type { WatchlistTier } from "@prisma/client";

interface SearchResult {
  ticker: string;
  exchange: string;
  companyName: string;
  currency: string;
  marketCap?: number;
}

interface AddStockDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAdded: () => void;
}

export function AddStockDialog({ isOpen, onClose, onAdded }: AddStockDialogProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<SearchResult | null>(null);
  const [tier, setTier] = useState<WatchlistTier>("monitor");
  const [conviction, setConviction] = useState<number>(5);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    setResults([]);
    setPicked(null);
    setTier("monitor");
    setConviction(5);
    setError(null);
    // Focus search after paint
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (picked) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/stocks/search?q=${encodeURIComponent(q)}`, {
          signal: controller.signal,
        });
        const json = await res.json();
        if (json.success) {
          setResults(json.data ?? []);
        }
      } catch {
        // Ignore — abort or transient error
      } finally {
        if (!controller.signal.aborted) {
          setSearching(false);
        }
      }
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [query, picked]);

  // Escape key closes
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  async function handleSubmit() {
    if (!picked) return;
    setSubmitting(true);
    setError(null);
    // Toast immediately on submit so the user knows their click
    // registered — the POST behind this can take several seconds
    // (FMP profile fetch + DB transaction + dashboard re-render).
    const toastId = toast.loading(`Adding ${picked.ticker} to your watchlist…`);
    try {
      const res = await fetch("/api/compass/watchlist", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ticker: picked.ticker,
          exchange: picked.exchange,
          tier,
          convictionUser: conviction,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        toast.error(json.error ?? "Could not add stock to watchlist.", {
          id: toastId,
        });
        setError(json.error ?? "Could not add stock to watchlist.");
        return;
      }
      toast.success(`${picked.ticker} added to ${tier} tier`, { id: toastId });
      onAdded();
      onClose();
    } catch {
      toast.error("Network error, please try again.", { id: toastId });
      setError("Network error, please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-stock-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-surface-light/30 rounded-2xl p-6 max-w-[520px] w-full mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="add-stock-title" className="text-foreground text-lg font-serif mb-4">
          Add stock to watchlist
        </h2>

        {!picked ? (
          <>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by ticker or company name..."
              aria-label="Search stocks"
              className="w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <div className="mt-3 max-h-64 overflow-y-auto" data-testid="search-results">
              {searching && query.trim().length >= 2 ? (
                <p className="text-muted-foreground text-xs px-2 py-2">Searching...</p>
              ) : null}
              {!searching && query.trim().length >= 2 && results.length === 0 ? (
                <p className="text-muted-foreground text-xs px-2 py-2">No matches.</p>
              ) : null}
              {results.map((r) => (
                <button
                  key={`${r.ticker}-${r.exchange}`}
                  type="button"
                  onClick={() => setPicked(r)}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-navy-mid transition-colors flex items-center justify-between gap-2 cursor-pointer border-none bg-transparent"
                >
                  <span className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">{r.ticker}</span>
                    <span className="text-xs text-muted-foreground line-clamp-1">{r.companyName}</span>
                  </span>
                  <span className="text-[11px] text-muted-foreground">{r.exchange}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="rounded-lg border border-surface-light/30 bg-navy-mid/40 px-3 py-2 mb-4 flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-foreground">
                  {picked.ticker} <span className="text-muted-foreground text-xs">· {picked.exchange}</span>
                </span>
                <span className="text-xs text-muted-foreground line-clamp-1">{picked.companyName}</span>
              </div>
              <button
                type="button"
                onClick={() => setPicked(null)}
                className="text-xs text-muted-foreground hover:text-foreground bg-transparent border-none cursor-pointer underline underline-offset-2"
              >
                Change
              </button>
            </div>

            <div className="flex flex-col gap-3 mb-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">Tier</span>
                <TierSelect value={tier} onChange={setTier} aria-label="Tier" />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">Conviction (1 to 10)</span>
                <ConvictionSlider value={conviction} onChange={setConviction} aria-label="Conviction" />
              </label>
            </div>

            {error ? (
              <p className="text-xs text-red-400 mb-3" role="alert">{error}</p>
            ) : null}

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 rounded-md border border-surface-light/30 bg-transparent text-sm text-muted-foreground hover:bg-navy-mid transition cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !picked}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue text-white text-sm font-semibold hover:opacity-90 transition cursor-pointer disabled:opacity-50 border-none"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Adding…
                  </>
                ) : (
                  "Add to watchlist"
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
